import React from "react";
import PropTypes from "prop-types";
import AniLink from "gatsby-plugin-transition-link/AniLink";

import TechnologyIcon from "../../components/technology/TechnologyIcon";
import {
  SectionActionButton,
  SectionBorderMagicButton,
} from "../../components/form/Button";
import { colors } from "../../../theme";
import { ProjectCard } from "../projects-section/project-card";
import {
  getProjectsForTechnology,
  getTechnologyPath,
} from "../../utils/constants/technologies";
import {
  TechBlock,
  TechIconSlot,
  InfoGrid,
  TitleSection,
  TechTitle,
  TechDescription,
  FeaturesGrid,
  FeatureBubble,
  FeatureBubbleFill,
  FeatureBubbleRing,
  FeatureBubbleText,
  CtaSlot,
  ProjectsBlock,
  ProjectsHeading,
  PortfolioGrid,
} from "./styles";

const TechnologySectionBlock = ({
  technology,
  titleAs = "h2",
  ctaMode = "view-more",
}) => {
  const projects = getProjectsForTechnology(technology);
  const isPageTitle = titleAs === "h1";
  const TitleTag = titleAs;
  const titleSize = isPageTitle ? "hero" : "section";
  const projectsHeadingAs = isPageTitle ? "h2" : "h3";
  const projectsHeadingSize = isPageTitle ? "section" : "subsection";
  const projectTitleAs = isPageTitle ? "h3" : "h4";
  const isViewMore = ctaMode === "view-more";
  const ctaTo = isViewMore
    ? getTechnologyPath(technology.slug)
    : "/contact-me/";
  const ctaLabel = isViewMore ? "View more" : "Contact me";
  const CtaButton = isViewMore ? SectionActionButton : SectionBorderMagicButton;

  return (
    <TechBlock id={technology.slug} aria-labelledby={`${technology.slug}-title`}>
      {technology.icon ? (
        <TechIconSlot>
          <TechnologyIcon name={technology.icon} />
        </TechIconSlot>
      ) : null}

      <InfoGrid>
        <TitleSection>
          <TechTitle
            as={TitleTag}
            $size={titleSize}
            id={`${technology.slug}-title`}
          >
            {technology.title}
          </TechTitle>
          {technology.description ? (
            <TechDescription>{technology.description}</TechDescription>
          ) : null}
        </TitleSection>

        {technology.features?.length > 0 ? (
          <FeaturesGrid>
            {technology.features.map((feature, index) => (
              <FeatureBubble key={feature} $index={index}>
                <FeatureBubbleFill />
                <FeatureBubbleRing />
                <FeatureBubbleText>{feature}</FeatureBubbleText>
              </FeatureBubble>
            ))}
          </FeaturesGrid>
        ) : null}

        <CtaSlot>
          <CtaButton
            forwardedAs={AniLink}
            cover
            bg={colors.black}
            to={ctaTo}
          >
            {ctaLabel}
          </CtaButton>
        </CtaSlot>
      </InfoGrid>

      {projects.length > 0 ? (
        <ProjectsBlock aria-labelledby={`${technology.slug}-projects-heading`}>
          <ProjectsHeading
            as={projectsHeadingAs}
            $size={projectsHeadingSize}
            id={`${technology.slug}-projects-heading`}
          >
            My top {technology.navLabel || technology.title} projects
          </ProjectsHeading>
          <PortfolioGrid>
            {projects.map((project) => (
              <ProjectCard
                key={project.slug}
                data={project}
                variant="compact"
                titleAs={projectTitleAs}
              />
            ))}
          </PortfolioGrid>
        </ProjectsBlock>
      ) : null}
    </TechBlock>
  );
};

TechnologySectionBlock.propTypes = {
  technology: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    navLabel: PropTypes.string,
    icon: PropTypes.string,
    description: PropTypes.string,
    features: PropTypes.arrayOf(PropTypes.string),
    projectSlugs: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  titleAs: PropTypes.string,
  ctaMode: PropTypes.oneOf(["view-more", "contact"]),
};

export default TechnologySectionBlock;
